import React from 'react';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  /** Defaults to 'lazy'. Pass 'eager' for above-the-fold images. */
  loading?: 'lazy' | 'eager';
}

/**
 * Renders a <picture> element that serves a WebP version of the image when
 * supported, falling back to the original JPEG/PNG. Lazy-loads by default.
 *
 * The WebP file is expected to exist at the same path with .webp extension,
 * generated by the optimize-images.js script.
 */
const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  loading = 'lazy',
  ...rest
}) => {
  const webpSrc = src.replace(/\.(jpe?g|png)(\?.*)?$/i, '.webp$2');
  const hasWebp = webpSrc !== src;

  if (!hasWebp) {
    return <img src={src} alt={alt} loading={loading} {...rest} />;
  }

  return (
    <picture>
      <source srcSet={webpSrc} type="image/webp" />
      <img src={src} alt={alt} loading={loading} {...rest} />
    </picture>
  );
};

export default OptimizedImage;
